/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, ChevronDown, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { Loading } from '../../components/Loading';
import { Foto } from '../Sustainability/comum';
import {
  anoDe,
  aplicarMetadados,
  carregarGaleria,
  ROTULOS,
  contagem,
  dataPorExtenso,
  destaquesDe,
  semAcento,
} from './dados';
import * as S from './style';

// Quantos cartões a lista mostra de cada vez.
const POR_VEZ = 12;

// O título com o trecho em destaque (verde, itálico). O trecho tem que estar
// escrito igual no título; se não estiver, o título sai todo normal.
export const TituloComDestaque = ({ titulo = '', destaque, as = 'h1' }) => {
  const i = destaque ? titulo.lastIndexOf(destaque) : -1;
  if (i < 0) return <S.Titulo as={as}>{titulo}</S.Titulo>;
  return (
    <S.Titulo as={as}>
      {titulo.slice(0, i)}
      <em>{destaque}</em>
      {titulo.slice(i + destaque.length)}
    </S.Titulo>
  );
};

// Foto que preenche um quadro (object-fit: cover). Se ela for em pé, o CSS
// puxa o recorte para o topo -- num quadro deitado, o centro cortaria rostos.
export const FotoEnquadrada = (props) => (
  <Foto
    {...props}
    onLoad={(e) => {
      const img = e.currentTarget;
      if (img.naturalHeight > img.naturalWidth) img.dataset.retrato = '';
    }}
  />
);

export const FaixaFinal = ({ faixa }) => {
  if (!faixa?.frase) return null;
  return (
    <S.FaixaFinal>
      <S.Container>
        <p>
          {faixa.frase.split('\n').map((linha, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {linha}
            </React.Fragment>
          ))}
        </p>
        {faixa.complemento && <span>{faixa.complemento}</span>}
      </S.Container>
    </S.FaixaFinal>
  );
};

export const CartaoAlbum = ({ album }) => (
  <S.Cartao as={Link} to={`/galeriaDeFotos/${album.endereco}`}>
    <S.CartaoFoto>
      <FotoEnquadrada id={album.capa} largura={720} alt="" />
      <S.Revelar aria-hidden="true">
        <span>
          {ROTULOS.verAlbum} <ArrowUpRight size={14} aria-hidden="true" />
        </span>
      </S.Revelar>
    </S.CartaoFoto>
    <S.Meta>
      <time dateTime={album.data.slice(0, 10)}>{dataPorExtenso(album.data)}</time>
      <span>
        {contagem(album.fotos.length, ROTULOS.foto)}
      </span>
    </S.Meta>
    <h3>{album.titulo}</h3>
    {album.descricao && <p>{album.descricao}</p>}
  </S.Cartao>
);

const Destaque = ({ album, etiqueta, principal }) => (
  <S.Destaque to={`/galeriaDeFotos/${album.endereco}`} $principal={principal}>
    <FotoEnquadrada
      id={album.capa}
      largura={principal ? 1400 : 800}
      alt=""
      loading={principal ? 'eager' : 'lazy'}
    />
    {etiqueta && <S.Etiqueta>{etiqueta}</S.Etiqueta>}
    <div>
      <time dateTime={album.data.slice(0, 10)}>{dataPorExtenso(album.data)}</time>
      <h2>{album.titulo}</h2>
      <S.Seta aria-hidden="true">
        <ArrowUpRight size={18} />
      </S.Seta>
    </div>
  </S.Destaque>
);

export const PhotoGallery = () => {
  const [galeria, setGaleria] = useState(null);
  const [pronto, setPronto] = useState(false);
  const [busca, setBusca] = useState('');
  const [ano, setAno] = useState(null);
  const [quantos, setQuantos] = useState(POR_VEZ);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    setLoading(true);
    if (!token) return;
    carregarGaleria(token)
      .then(setGaleria)
      .catch((e) => console.log(e.message))
      .finally(() => {
        setPronto(true);
        setLoading(false);
      });
  }, [token]);

  // A página tem o próprio topo: o banner do Header fica desligado.
  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  useEffect(() => {
    if (!galeria) return undefined;
    return aplicarMetadados(galeria.metadados);
  }, [galeria]);

  // Do mais recente ao mais antigo (a lista já vem por data).
  const anos = useMemo(
    () => [...new Set((galeria?.lista || []).map((a) => anoDe(a.data)))].filter(Boolean),
    [galeria],
  );

  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return (galeria?.lista || []).filter(
      (a) =>
        (!ano || anoDe(a.data) === ano) &&
        (!termo || semAcento(`${a.titulo} ${a.descricao}`).includes(termo)),
    );
  }, [galeria, busca, ano]);

  // Filtro novo começa do primeiro bloco de novo.
  useEffect(() => setQuantos(POR_VEZ), [busca, ano]);

  if (!pronto) return <Loading />;
  if (!galeria) return null;

  const { topo, albuns: secaoLista } = galeria;
  const destaques = destaquesDe(galeria);
  const visiveis = filtrados.slice(0, quantos);
  const limpar = () => {
    setBusca('');
    setAno(null);
  };

  return (
    <S.Pagina>
      <S.Container>
        <S.Abertura>
          <div>
            <S.TrilhaDaPagina />
            <TituloComDestaque titulo={topo.titulo} destaque={topo.destaque} />
          </div>
          {topo.descricao && <p>{topo.descricao}</p>}
        </S.Abertura>

        {destaques.length > 0 && (
          <S.Destaques $quantos={destaques.length}>
            {destaques.map((album, i) => (
              <Destaque
                key={album.id}
                album={album}
                principal={i === 0}
                etiqueta={i === 0 ? ROTULOS.destaque : null}
              />
            ))}
          </S.Destaques>
        )}
      </S.Container>

      <S.Lista>
        <S.Container>
          <S.BarraDaSecao>
            <h2>{secaoLista.titulo}</h2>
            <small>{ROTULOS.chapeuLista}</small>
          </S.BarraDaSecao>

          <S.Ferramentas>
            <S.Busca>
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder={ROTULOS.busca}
                aria-label={ROTULOS.busca}
              />
              {/* o X nativo do campo de busca não aceita cor: este é o nosso, igual à lupa */}
              {busca && (
                <button type="button" aria-label="Limpar busca" onClick={() => setBusca('')}>
                  <X size={18} />
                </button>
              )}
            </S.Busca>
            {anos.length > 0 && (
              <S.Anos role="group" aria-label={secaoLista.titulo}>
                <button type="button" aria-pressed={!ano} onClick={() => setAno(null)}>
                    {ROTULOS.todos}
                </button>
                {/* os dois anos mais recentes viram botão; os anteriores, uma lista */}
                {anos.slice(0, 2).map((a) => (
                  <button key={a} type="button" aria-pressed={ano === a} onClick={() => setAno(a)}>
                    {a}
                  </button>
                ))}
                {anos.length > 2 && (
                  <S.OutrosAnos $ativo={anos.slice(2).includes(ano)}>
                    <select
                      value={anos.slice(2).includes(ano) ? ano : ''}
                      onChange={(e) => setAno(e.target.value || null)}
                      aria-label={ROTULOS.outrosAnos}
                    >
                      <option value="">{ROTULOS.outrosAnos}</option>
                      {anos.slice(2).map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} aria-hidden="true" />
                  </S.OutrosAnos>
                )}
              </S.Anos>
            )}
            <S.Contagem aria-live="polite">
              {contagem(filtrados.length, ROTULOS.album)}
            </S.Contagem>
          </S.Ferramentas>

          {visiveis.length ? (
            <S.Grade>
              {visiveis.map((album) => (
                <CartaoAlbum key={album.id} album={album} />
              ))}
            </S.Grade>
          ) : (
            <S.Vazio>
              <p>{ROTULOS.vazio}</p>
              <button type="button" onClick={limpar}>
                  {ROTULOS.limpar}
              </button>
            </S.Vazio>
          )}

          {filtrados.length > quantos && (
            <S.CarregarMais type="button" onClick={() => setQuantos(quantos + POR_VEZ)}>
              {ROTULOS.carregarMais}
            </S.CarregarMais>
          )}
          {visiveis.length > 0 && filtrados.length <= quantos && (
            <S.Fim>{ROTULOS.fim}</S.Fim>
          )}
        </S.Container>
      </S.Lista>

      <FaixaFinal faixa={galeria.faixaFinal} />
    </S.Pagina>
  );
};
